import {
  Typography,
  ListItem,
  Box,
  useTheme,
  Stack,
  Grid,
} from "@mui/material";
import { Link } from "react-router-dom";
import convertToViewportUnits from "../../helpers/convertToViewportUnits";
import Person2Icon from "@mui/icons-material/Person2";
import contributorsData from "../../data/contributors.json";
import ProfileCard from "./components/ProfileCard";
import ContributorCard from "./components/ContributorCard";
import { useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";

const { leads, advisors, primary_contributors, general_contributors } =
  contributorsData;

const countryCoordinates: Record<string, [number, number]> = {
  CA: [-106.3468, 56.1304],
  CH: [8.2275, 46.8182],
  US: [-98.5795, 39.8283],
  IN: [78.9629, 20.5937],
  ES: [-3.7492, 40.4637],
  PL: [19.1451, 51.9194],
  PK: [69.3451, 30.3753],
  DE: [10.4515, 51.1657],
  CN: [104.1954, 35.8617],
  LT: [23.8813, 55.1694],
  SA: [45.0792, 23.8859],
  FR: [2.2137, 46.6034],
  AU: [133.7751, -25.2744],
  ZA: [22.9375, -30.5595],
  PH: [122.5583, 10.3144],
  JP: [137.726, 35.4437],
  EG: [31.2357, 30.0444],
};

type Contributor = {
  name: string;
  country_code: keyof typeof countryCoordinates;
  personal_website: string | null;
  affiliate_title: string;
  affiliate: string;
};

const Contributors = () => {
  const theme = useTheme();
  const [allContributors, setAllContributors] = useState<Contributor[]>([]);
  const [loading, setLoading] = useState(true);

  // fetching all the contributors on intial render
  useEffect(() => {
    const fetchContributors = async () => {
      try {
        const contributors = [
          ...leads,
          ...advisors,
          ...primary_contributors,
          ...general_contributors,
        ];
        setAllContributors(contributors); // combining all the contributors into one array
      } catch (error) {
        console.error("Error fetching contributors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContributors(); // calling fetch function
  }, []);

  // setting up Mapbox map
  useEffect(() => {
    if (loading || allContributors.length === 0) return;

    // access token from An -- default token scopes
    mapboxgl.accessToken = "pk.eyJ1IjoiYW5kaW5oIiwiYSI6ImNtNmIzNmZ5MTA0OXYyam84YXg0eTZ2dmgifQ.ghNS_DgKfPf52NziDo5BLg";

    const map = new mapboxgl.Map({
      container: "map",
      style: "mapbox://styles/mapbox/streets-v11",
      center: [-74.5, 40],
      zoom: 3,
      attributionControl: false,
    });

    const markers = addMarkers(map, allContributors);

    map.on("zoomend", () => {
      const zoomLevel = map.getZoom();
      const scaleFactor = Math.pow(1.2, zoomLevel - 3);
      markers.forEach((marker) => {
        const markerElement = marker.getElement();
        if (markerElement) {
          markerElement.style.transform = `scale(${scaleFactor})`; // scaling marker size
        }
      });
    });

    return () => {
      map.remove();
    };
  }, [loading, allContributors]);

  // function to add markers
  const addMarkers = (map: mapboxgl.Map, contributors: Contributor[]) => {
    const bounds = new mapboxgl.LngLatBounds();
    const markers: mapboxgl.Marker[] = [];

    // group contributors by country code
    const groupedContributors = groupContributorsByCountry(contributors);

    const fixedSize = 30;  // Define fixed size here, no need to define it in createMarker

    // add markers for each country
    Object.entries(groupedContributors).forEach(([countryCode, countryContributors]) => {
      const coordinates = countryCoordinates[countryCode];
      if (!coordinates) return;

      const [longitude, latitude] = coordinates;
      const marker = createMarker(map, longitude, latitude, fixedSize);  // Use fixedSize from above

      // popup content
      const popupContent = getPopupContent(countryCode, countryContributors);
      const popup = createPopup(popupContent);

      marker.setPopup(popup);
      bounds.extend([longitude, latitude]);
      markers.push(marker);
    });

    map.fitBounds(bounds, { padding: 50 });
    return markers;
  };

  // HELPER FUNCTIONS
  const groupContributorsByCountry = (contributors: Contributor[]) => {
    return contributors.reduce((acc, contributor) => {
      acc[contributor.country_code] = acc[contributor.country_code] || [];
      acc[contributor.country_code].push(contributor);
      return acc;
    }, {} as Record<string, Contributor[]>);
  };

  const createMarker = (map: mapboxgl.Map, longitude: number, latitude: number, size: number) => {
    const marker = new mapboxgl.Marker({
      color: theme.customColors.primary.prusianBlue,
      draggable: false,
      anchor: "bottom", // anchor the marker to the bottom point
    })
      .setLngLat([longitude, latitude])
      .addTo(map);

    // apply fixed size to markers
    const markerElement = marker.getElement();
    if (markerElement) {
      markerElement.style.transform = `scale(${size / 20})`;
    }

    return marker;
  };

  const getPopupContent = (countryCode: string, countryContributors: Contributor[]) => {
    const contributorNames = countryContributors.map((c) => c.name).join(", ");
    return `<strong>${countryCode}</strong>: ${contributorNames}`;
  };

  const createPopup = (content: string) => {
    return new mapboxgl.Popup({
      offset: 25,
      maxWidth: '300px',
      closeButton: true,
      className: 'custom-popup',
    }).setHTML(content);
  };

  return (
    <>
      <Box sx={{ width: "100%", overflow: "auto", padding: { lg: "80px 0 125px", sm: "80px 0 100px", xs: "50px 0 80px" } }}>
        <Grid container spacing={3}>
          <Grid item md={12} xs={12}>
            <Box sx={{ marginBottom: { lg: "50px", md: "30px", xs: "20px" } }}>
              <Typography
                sx={{
                  fontSize: { lg: "96px", md: "80px", sm: "60px", xs: "48px" },
                  lineHeight: '100%',
                  fontWeight: '500',
                  color: theme.customColors.primary.prusianBlue,
                }}
                variant="h1"
              >Contributors</Typography>
            </Box>
          </Grid>
          <Grid item md={12} xs={12}>
            <Box sx={{ borderTop: '1px solid', borderTopColor: theme.customColors.primary.prusianBlue, padding: { md: "40px 0 60px", xs: "40px 0 40px" } }}>
              <Box sx={{ marginBottom: { lg: "60px", md: "40px", xs: "20px" } }}>
                <Typography sx={{
                  fontSize: { lg: "36px", md: "30px", xs: "24px" }, fontWeight: '700',
                  color: theme.customColors.primary.prusianBlue, marginBottom: '0', lineHeight: '1.3'
                }} variant="h3">Leads</Typography>
              </Box>
              <Box sx={{ width: "100%", overflow: "auto" }}>
                <Grid
                  spacing={{ xs: 3, md: 5, lg: 7 }}
                  container
                >
                  {leads.map((item, index) => (
                    <Grid item xs={6} sm={4} lg={3}>
                      <ProfileCard
                        item={{ ...item }}
                        key={index}
                      ></ProfileCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid', borderTopColor: theme.customColors.primary.prusianBlue, padding: { md: "40px 0 60px", xs: "40px 0 40px" } }}>
              <Box sx={{ marginBottom: { lg: "60px", md: "40px", xs: "20px" } }}>
                <Typography sx={{
                  fontSize: { lg: "36px", md: "30px", xs: "24px" }, fontWeight: '700',
                  color: theme.customColors.primary.prusianBlue, marginBottom: '0', lineHeight: '1.3'
                }} variant="h3">Advisors</Typography>
              </Box>
              <Box sx={{ width: "100%", overflow: "auto" }}>
                <Grid
                  spacing={{ xs: 3, md: 5, lg: 7 }}
                  container
                >

                  {advisors.map((item, index) => (
                    <Grid item xs={6} sm={4} lg={3}>
                      <ProfileCard item={{ ...item }} key={index}></ProfileCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid', borderTopColor: theme.customColors.primary.prusianBlue, padding: { md: "40px 0 60px", xs: "40px 0 40px" } }}>
              <Box sx={{ marginBottom: { lg: "60px", md: "40px", xs: "20px" } }}>
                <Typography sx={{
                  fontSize: { lg: "36px", md: "30px", xs: "24px" }, fontWeight: '700',
                  color: theme.customColors.primary.prusianBlue, marginBottom: '0', lineHeight: '1.3'
                }} variant="h3">Primary Contributors</Typography>
              </Box>
              <Box sx={{ width: "100%", overflow: "auto" }}>
                <Grid
                  spacing={{ xs: 3, md: 5, lg: 7 }}
                  container
                >

                  {primary_contributors.map((item, index) => (
                    <Grid item xs={6} sm={4} lg={3}>
                      <ContributorCard item={item} key={index} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
            <Box sx={{ borderTop: '1px solid', borderTopColor: theme.customColors.primary.prusianBlue, padding: '40px 0 0px' }}>
              <Box sx={{ marginBottom: { lg: "60px", md: "40px", xs: "20px" } }}>
                <Typography sx={{
                  fontSize: { lg: "36px", md: "30px", xs: "24px" }, fontWeight: '700',
                  color: theme.customColors.primary.prusianBlue, marginBottom: '0', lineHeight: '1.3'
                }} variant="h3">Contributors</Typography>
              </Box>
              <Box sx={{ width: "100%", overflow: "auto" }}>
                <Grid
                  spacing={{ xs: 3, md: 5, lg: 7 }}
                  container
                >

                  {general_contributors.map((item, index) => (
                    <Grid item xs={6} sm={4} lg={3} >
                      <ContributorCard item={item} key={index} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ display: 'none' }}>
        <Box id="map" sx={{ width: "100%", height: "450px", borderRadius: "8px", paddingBottom: "100px", marginBottom: "20px" }} />
      </Box>
    </>
  );
};

export default Contributors;
